import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import "./QuickStart.css";
import { Link } from "react-router-dom";

const Section = ({ title, icon, isOpen, onClick, children }) => (
  <div className={`guide-section ${isOpen ? "open" : ""}`}>
    <button className="section-header" onClick={onClick}>
      <span className="section-icon">{icon}</span>
      <span className="section-title">{title}</span>
      <span className="section-arrow">{isOpen ? "▼" : "▶"}</span>
    </button>
    {isOpen && <div className="section-content">{children}</div>}
  </div>
);

const CommonInfo = ({ openSections, toggleSection }) => (
  <>
    <Section
      title="Mobile Access"
      icon="📱"
      isOpen={openSections.mobile}
      onClick={() => toggleSection("mobile")}
    >
      <div className="section-block">
        <h3 style={{ color: "var(--text)" }}>
          You can use Cinesis just like an app!
        </h3>
        <p>Follow these steps to install it to your phone:</p>

        <div className="install-guide">
          <h4>✅ 1️⃣ For iPhone (Safari)</h4>
          <ol>
            <li>Open Safari on your iPhone.</li>
            <li>
              Go to this link:
              <br />
              👉{" "}
              <a
                href="https://helpful-hamster-d11f17.netlify.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                helpful-hamster-d11f17.netlify.app
              </a>
            </li>
            <li>
              Tap the Share button (the square with an arrow pointing up).
            </li>
            <li>Scroll down in the menu.</li>
            <li>Tap Add to Home Screen.</li>
            <li>Optionally, rename it (e.g., Cinesis).</li>
            <li>Tap Add in the upper right corner.</li>
          </ol>
          <p>
            ✅ Done! Now you'll see the Cinesis icon on your home screen. Tap it
            to launch.
          </p>
        </div>

        <div className="install-guide">
          <h4>✅ 2️⃣ For Android (Chrome)</h4>
          <ol>
            <li>Open Chrome on your Android phone.</li>
            <li>
              Go to this link:
              <br />
              👉{" "}
              <a
                href="https://helpful-hamster-d11f17.netlify.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                helpful-hamster-d11f17.netlify.app
              </a>
            </li>
            <li>Tap the Menu button (3 dots in the top-right corner).</li>
            <li>Tap Add to Home screen or Install App.</li>
            <li>Optionally, rename it (e.g., Cinesis).</li>
            <li>Tap Add.</li>
          </ol>
          <p>
            ✅ That's it! You'll now have a Cinesis icon on your home screen.
          </p>
        </div>

        <div className="tip-section">
          <p>
            ✨ <strong>Tip</strong>
          </p>
          <p>
            Once installed, Cinesis will open in a clean app-like view without
            browser controls.
          </p>
        </div>
      </div>
    </Section>

    <div className="static-section">
      <div className="section-header">
        <span className="section-icon">💬</span>
        <span className="section-title">Support</span>
      </div>
      <div className="section-content">
        <div className="section-block">
          <p>
            Feedback or questions? Email:{" "}
            <a href="mailto:loadfinder@cinesis.ai">loadfinder@cinesis.ai</a>
          </p>
          <p>
            You can also&nbsp;
            <Link to="/contact-us" style={{ color: "var(--primary)" }}>
              submit feedback or report an issue here
            </Link>
            .
          </p>
        </div>
      </div>
    </div>

    <div className="static-section">
      <div className="section-header">
        <span className="section-icon">🔜</span>
        <span className="section-title">Future Features Coming Soon</span>
      </div>
      <div className="section-content">
        <div className="section-block">
          <ul>
            <ul>
              <li>Automated load booking with our AI Voice Agent</li>
              <li>Seamless document management and rate confirmation tools</li>
              <li>More load board integrations to expand your options</li>
            </ul>
          </ul>
        </div>
      </div>
    </div>
  </>
);

const QuickStart = () => {
  const { user } = useAuth();
  const accountType = user?.account_type;
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  switch (accountType) {
    case "carrier":
      return (
        <CarrierGuide
          openSections={openSections}
          toggleSection={toggleSection}
        />
      );
    case "dispatcher":
      return (
        <DispatcherGuide
          openSections={openSections}
          toggleSection={toggleSection}
        />
      );
    case "driver":
      return (
        <DriverGuide
          openSections={openSections}
          toggleSection={toggleSection}
        />
      );
    default:
      return (
        <CarrierGuide
          openSections={openSections}
          toggleSection={toggleSection}
        />
      );
  }
};

export default QuickStart;

function CarrierGuide({ openSections, toggleSection }) {
  return (
    <div className="quick-start">
      <div className="guide-header">
        <h1>🚛 Quick Start for Carriers</h1>
        <div className="guide-intro">
          <h2>✨ What You Can Do:</h2>
          <ul>
            <li>Search and book loads yourself</li>
            <li>Assign loads to your drivers</li>
            <li>Work with dispatchers who can suggest and book loads</li>
            {/* <li>Use the AI Voice Agent to get load suggestions</li> */}
            <li>Manage saved and booked loads</li>
          </ul>
        </div>
      </div>

      <Section
        title="Managing Drivers"
        icon="1️⃣"
        isOpen={openSections.drivers}
        onClick={() => toggleSection("drivers")}
      >
        <div className="section-block">
          <h3>Overview:</h3>
          <ul>
            <li>Drivers can receive loads you assign them</li>
            <li>Drivers do not pay any account fee</li>
          </ul>
        </div>
        <div className="section-block">
          <h3>Steps:</h3>
          <ol>
            <li>Have your drivers sign up on Cinesis as Driver</li>
            <li>Go to Drivers Page</li>
            <li>Add your drivers to your account</li>
            <li>
              When you book a load, go to Booked Loads, assign it to a driver
            </li>
            <li>Drivers will see load details in their account</li>
          </ol>
        </div>
      </Section>

      <Section
        title="Searching and Booking Loads"
        icon="2️⃣"
        isOpen={openSections.searching}
        onClick={() => toggleSection("searching")}
      >
        <div className="section-block">
          <h3>Steps:</h3>
          <ol>
            <li>Go to Load Search</li>
            <li>Add all your truck information</li>
            <li>Enable Backhaul Search if needed</li>
            <li>Use Save to keep loads you like</li>
          </ol>
        </div>
        <div className="section-block">
          <h3>To book manually:</h3>
          <ol>
            <li>Call the broker</li>
            <li>Press Book in the app</li>
            <li>Booked loads appear in Booked Loads Page</li>
            <li>You can assign booked loads to your driver as well</li>
          </ol>
        </div>
      </Section>

      {/* <Section
        title="Using the AI Voice Agent"
        icon="3️⃣"
        isOpen={openSections.voiceAgent}
        onClick={() => toggleSection("voiceAgent")}
      >
        <div className="section-block">
          <h3>Overview:</h3>
          <ul>
            <li>Automatically finds top 3 loads for you</li>
            <li>Currently beta; cannot book loads yet</li>
          </ul>
        </div>
        <div className="section-block">
          <h3>Steps:</h3>
          <ol>
            <li>Enter your truck info and city location</li>
            <li>Press Call Now</li>
            <li>The AI Voice Agent will call you with load options</li>
          </ol>
          <p className="note">Note: Booking capability coming soon!</p>
        </div>
      </Section> */}

      <Section
        title="Working with Dispatchers"
        icon="3️⃣"
        isOpen={openSections.dispatchers}
        onClick={() => toggleSection("dispatchers")}
      >
        <div className="section-block">
          <h3>Overview:</h3>
          <p>Dispatchers can find and book loads on your behalf</p>
        </div>
        <div className="section-block">
          <h3>Steps:</h3>
          <ol>
            <li>Ask your dispatcher to sign up as Dispatcher</li>
            <li>No Truckstop account required</li>
            <li>Go to Dispatcher Page</li>
            <li>Add them as your dispatcher</li>
          </ol>
        </div>
        <div className="section-block">
          <h3>Dispatcher can:</h3>
          <ul>
            <li>Search and save loads</li>
            <li>Suggest loads to you (accept or reject)</li>
            <li>Book loads for you</li>
            <li>Accepted loads appear in their saved loads page</li>
            <li>
              When dispatcher books a load, it appears in your Booked Loads
            </li>
          </ul>
        </div>
      </Section>

      <div className="common-info">
        <h2>Additional Information</h2>
        <CommonInfo openSections={openSections} toggleSection={toggleSection} />
      </div>
    </div>
  );
}

function DispatcherGuide({ openSections, toggleSection }) {
  return (
    <div className="quick-start">
      <div className="guide-header">
        <h1>🧑‍💼 Quick Start for Dispatchers</h1>
        <div className="guide-intro">
          <h2>✨ What You Can Do:</h2>
          <ul>
            <li>
              Bring carriers to Cinesis and get $10 monthly discount per carrier
            </li>
            <li>Search loads for carriers</li>
            <li>Suggest loads to carriers</li>
            <li>Book loads on their behalf</li>
          </ul>
        </div>
      </div>

      <Section
        title="Bringing Carriers to Cinesis"
        icon="1️⃣"
        isOpen={openSections.onboarding}
        onClick={() => toggleSection("onboarding")}
      >
        <div className="section-block">
          <h3>Benefits:</h3>
          <ul>
            <li>$10/month discount per carrier you bring</li>
          </ul>
        </div>
        <div className="section-block">
          <h3>Steps:</h3>
          <ol>
            <li>Have carriers sign up as Carrier</li>
            <li>
              Carriers must set up Truckstop account and get Integration ID
              (Instructions in Profile Page)
            </li>
            <li>Ask them to add you in their Dispatcher Page</li>
            <li>You can now search and book loads for them</li>
          </ol>
        </div>
      </Section>

      <Section
        title="Searching and Suggesting Loads"
        icon="2️⃣"
        isOpen={openSections.searching}
        onClick={() => toggleSection("searching")}
      >
        <div className="section-block">
          <h3>Steps:</h3>
          <ol>
            <li>Go to Load Search</li>
            <li>Select the carrier</li>
            <li>Enter their truck information</li>
            <li>Enable Backhaul Search if needed</li>
            <li>Save loads you like</li>
            <li>In Saved Loads, use Assign to... to suggest loads</li>
            <li>The carrier will accept or reject the load</li>
            <li>Accepted loads stay in your saved loads, ready to book</li>
          </ol>
        </div>
      </Section>

      <Section
        title="Booking Loads for Carriers"
        icon="3️⃣"
        isOpen={openSections.booking}
        onClick={() => toggleSection("booking")}
      >
        <div className="section-block">
          <h3>Steps:</h3>
          <p>Once the carrier accepts a load:</p>
          <ol>
            <li>Call the broker to book</li>
            <li>In Saved Loads, press Book</li>
            <li>
              Load will appear in both your Booked Loads and the carrier's
              Booked Loads
            </li>
          </ol>
        </div>
      </Section>

      <div className="common-info">
        <h2>Additional Information</h2>
        <CommonInfo openSections={openSections} toggleSection={toggleSection} />
      </div>
    </div>
  );
}

function DriverGuide({ openSections, toggleSection }) {
  return (
    <div className="quick-start">
      <div className="guide-header">
        <h1>🚚 Quick Start for Drivers</h1>
        <div className="guide-intro">
          <h2>✨ What You Can Do:</h2>
          <ul>
            <li>Receive loads assigned by carriers</li>
            <li>View load information in your account</li>
          </ul>
        </div>
      </div>

      <Section
        title="Getting Started"
        icon="1️⃣"
        isOpen={openSections.gettingStarted}
        onClick={() => toggleSection("gettingStarted")}
      >
        <div className="section-block">
          <h3>Steps:</h3>
          <ol>
            <li>Sign up as Driver</li>
            <li>No account fee</li>
            <li>No Truckstop account required</li>
            <li>Ask your carrier to add you in their Drivers Page</li>
          </ol>
        </div>
      </Section>

      <Section
        title="Receiving Loads"
        icon="2️⃣"
        isOpen={openSections.receiving}
        onClick={() => toggleSection("receiving")}
      >
        <div className="section-block">
          <h3>Steps:</h3>
          <ol>
            <li>
              When your carrier assigns you a load, it appears in your account
            </li>
            <li>You can see all load details for planning</li>
          </ol>
          <p className="note">
            Note: Drivers cannot search or book loads themselves.
          </p>
        </div>
      </Section>

      <div className="common-info">
        <h2>Additional Information</h2>
        <CommonInfo openSections={openSections} toggleSection={toggleSection} />
      </div>
    </div>
  );
}
